import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import * as m from '@mui/material'

import * as cpm from '../../../app/components'
import * as gs from '../../../app/gStyles'

export type ContractLogoProps = c.r<{
  contractCompanyFull: c.ContractCompany
  isPDF?: boolean
  onChangeContractCompany?(e: { target: { value: string } }): void
  options: c.ContractCompany[]
  size: 's' | 'm' | 'l'
}>

export default React.memo<ContractLogoProps>(function ContractLogo({
  contractCompanyFull,
  isPDF,
  onChangeContractCompany,
  options,
  size,
}) {
  const companySelected = !!contractCompanyFull.shortName
  const companySelectedIsInCurrList = React.useMemo(
    (): boolean =>
      !!options.find(
        ({ shortName }) => shortName === contractCompanyFull.shortName,
      ),
    [contractCompanyFull.shortName, options],
  )

  const [choosingCompany, , toggleChoosingCompany] = r.useBool(false)

  const disabled = !onChangeContractCompany || isPDF || options.length === 0

  const valueRenderer = React.useCallback(
    () => (
      <rn.View style={gs.rowCentered}>
        {companySelected && companySelectedIsInCurrList ? (
          <img
            alt={contractCompanyFull.shortName}
            src={contractCompanyFull.logo}
            style={(() => {
              if (size === 'l') return sx.logoLarge
              if (size === 'm') return sx.logoMedium
              return sx.logoSmall
            })()}
          />
        ) : (
          <cpm.Text colorScheme="light">
            Click here to select a company for the contract
          </cpm.Text>
        )}
      </rn.View>
    ),
    [
      companySelected,
      companySelectedIsInCurrList,
      contractCompanyFull.logo,
      contractCompanyFull.shortName,
      size,
    ],
  )

  if (isPDF) {
    return valueRenderer()
  }

  return (
    <>
      <m.Select
        IconComponent={disabled ? ((() => null) as any) : undefined}
        disabled={disabled}
        displayEmpty
        sx={disabled ? sx.disabledSelect : sx.select}
        onChange={onChangeContractCompany}
        onClose={toggleChoosingCompany}
        onOpen={toggleChoosingCompany}
        open={choosingCompany}
        renderValue={valueRenderer}
        value={contractCompanyFull.shortName}
      >
        {(options || []).map(({ logo, longName, shortName }) => (
          <m.MenuItem key={shortName} style={sx.menuItem} value={shortName}>
            <img alt={longName} src={logo} style={{ width: 100, height: 40 }} />

            <rn.View style={gs.rowCentered}>
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={18}>
                {longName}
              </cpm.Text>
            </rn.View>

            {gs.rowGap8}
          </m.MenuItem>
        ))}
      </m.Select>
    </>
  )
})

const sx = {
  logoLarge: { width: 200 },
  logoMedium: { width: 128 },
  logoSmall: { width: 80 },
  menuItem: {
    alignItems: 'stretch',
    flexDirection: 'row',
    paddingBottom: 24,
    paddingTop: 24,
  } as const,
  select: {
    '& .MuiOutlinedInput-notchedOutline, & fieldset': { border: 'none' },
  } as const,
  disabledSelect: {
    '& .MuiSelect-select': { cursor: 'default' },
    '& .MuiOutlinedInput-notchedOutline, & fieldset': { border: 'none' },
    '& .MuiSvgIcon-root': { display: 'none' },
  } as const,
}
